import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck, IconQrcode, IconUnlink } from '@tabler/icons-react'
import { whatsappGatewaysApi, type Espera } from '@/api/whatsappGateways'
import { useDesparear } from '@/hooks/useWhatsappGateways'
import { mensagemDeErro } from './mensagens'

/**
 * QR de pareamento e desparear de um gateway (spec §4/§4.1).
 *
 * O QR é credencial: vive SÓ no estado deste componente. Não passa por
 * useQuery/useMutation, porque o cache do react-query o guardaria depois de a
 * tela fechar.
 *
 * Laço de espera: cada `aguardarPareamento` segura até 25 s no servidor e manda
 * o QR que está na tela (`qr_atual`), sem o qual o gateway nunca devolve o
 * renovado. Para em `pareado`, `expirado`, `sem_login`, erro, desmontagem, e na
 * TERCEIRA renovação sem leitura (spec §4: QR não fica em laço). A terceira não
 * é mostrada: a tela oferece gerar outro.
 *
 * Geração: `gerar()` e o desparear com sucesso incrementam `geracao` e abortam a
 * espera pendente. O laço de `esperar()` carrega a geração com que nasceu e some
 * sem tocar a tela assim que ela deixa de ser a atual — sem isso, a resposta de
 * uma espera velha (até 40 s depois, `TIMEOUT_ESPERA_MS`) sobrescreveria a tela
 * já desparelhada ou já num pareamento novo (review Task 11, achado 1).
 */
export const MAX_RENOVACOES = 3
const PAUSA_AGUARDANDO_MS = 1_000

type Motivo = 'expirado' | 'renovacoes' | 'sem_login' | 'erro'
type Fase =
  | { tipo: 'parado' }
  | { tipo: 'gerando' }
  | { tipo: 'mostrando'; qr: string }
  | { tipo: 'pareado' }
  | { tipo: 'encerrado'; motivo: Motivo; detalhe?: string }

const TEXTO_ENCERRADO: Record<Motivo, string> = {
  expirado: 'O QR expirou sem leitura.',
  renovacoes: 'O QR foi renovado 3 vezes sem leitura e a espera parou.',
  sem_login: 'Não há pareamento em andamento neste gateway. Se outra pessoa acabou de parear, confira a trilha.',
  erro: 'A espera pelo pareamento falhou.',
}

export function PareamentoDoGateway({ orgId, gwId, onPareado }: { orgId: number; gwId: string; onPareado?: () => void }) {
  const [fase, setFase] = useState<Fase>({ tipo: 'parado' })
  const [confirmar, setConfirmar] = useState(false)
  const vivo = useRef(true)
  const espera = useRef<AbortController | null>(null)
  const geracao = useRef(0)
  const desparear = useDesparear(orgId, gwId)

  useEffect(() => {
    vivo.current = true
    return () => {
      vivo.current = false
      espera.current?.abort()
    }
  }, [])

  async function esperar(qrInicial: string, minhaGeracao: number) {
    let qr = qrInicial
    let renovacoes = 0
    const minhaVez = () => vivo.current && geracao.current === minhaGeracao
    while (minhaVez()) {
      const ctrl = new AbortController()
      espera.current = ctrl
      let r: Espera
      try {
        r = await whatsappGatewaysApi.aguardarPareamento(orgId, gwId, qr, ctrl.signal)
      } catch (e) {
        if (minhaVez()) setFase({ tipo: 'encerrado', motivo: 'erro', detalhe: mensagemDeErro(e) })
        return
      }
      if (!minhaVez()) return
      if (r.estado === 'aguardando') {
        await new Promise((resolve) => setTimeout(resolve, PAUSA_AGUARDANDO_MS))
        continue
      }
      if (r.estado === 'renovado' && r.qr_data_url) {
        renovacoes += 1
        if (renovacoes >= MAX_RENOVACOES) {
          setFase({ tipo: 'encerrado', motivo: 'renovacoes' })
          return
        }
        qr = r.qr_data_url
        setFase({ tipo: 'mostrando', qr })
        continue
      }
      if (r.estado === 'pareado') {
        setFase({ tipo: 'pareado' })
        onPareado?.()
        return
      }
      setFase({ tipo: 'encerrado', motivo: r.estado === 'sem_login' ? 'sem_login' : 'expirado' })
      return
    }
  }

  async function gerar() {
    espera.current?.abort()
    const minhaGeracao = ++geracao.current
    setFase({ tipo: 'gerando' })
    try {
      const r = await whatsappGatewaysApi.iniciarPareamento(orgId, gwId)
      if (!vivo.current || geracao.current !== minhaGeracao) return
      setFase({ tipo: 'mostrando', qr: r.qr_data_url })
      void esperar(r.qr_data_url, minhaGeracao)
    } catch (e) {
      if (vivo.current && geracao.current === minhaGeracao) {
        setFase({ tipo: 'encerrado', motivo: 'erro', detalhe: mensagemDeErro(e) })
      }
    }
  }

  function confirmarDesparear() {
    espera.current?.abort()
    geracao.current += 1
    desparear.mutate(undefined, {
      onSuccess: () => {
        setConfirmar(false)
        setFase({ tipo: 'parado' })
      },
    })
  }

  return (
    <Stack gap="sm">
      <Title order={4}>Pareamento</Title>
      {fase.tipo === 'parado' && (
        <Group>
          <Button leftSection={<IconQrcode size={16} />} onClick={gerar}>Gerar QR</Button>
        </Group>
      )}
      {fase.tipo === 'gerando' && <Text size="sm">Pedindo o QR ao gateway…</Text>}
      {fase.tipo === 'mostrando' && (
        <Stack gap="xs" align="flex-start">
          <Text size="sm">No celular: WhatsApp → Aparelhos conectados → Conectar um aparelho, e leia o código.</Text>
          {/* Tamanho nativo do PNG que o OpenClaw gera: escalar borra os módulos do QR. */}
          <img src={fase.qr} alt="QR de pareamento do WhatsApp" />
        </Stack>
      )}
      {fase.tipo === 'pareado' && (
        <Alert color="green" variant="light" icon={<IconCircleCheck size={16} />}>
          Pareado. O WhatsApp da organização está ligado a este gateway.
        </Alert>
      )}
      {fase.tipo === 'encerrado' && (
        <Stack gap="xs" align="flex-start">
          <Alert color={fase.motivo === 'erro' ? 'red' : 'yellow'} variant="light" icon={<IconAlertTriangle size={16} />}>
            {TEXTO_ENCERRADO[fase.motivo]}{fase.detalhe ? ` ${fase.detalhe}` : ''}
          </Alert>
          <Button variant="outline" leftSection={<IconQrcode size={16} />} onClick={gerar}>Gerar novo QR</Button>
        </Stack>
      )}
      <Group>
        <Button variant="outline" color="red" leftSection={<IconUnlink size={16} />} onClick={() => setConfirmar(true)}>
          Desparear
        </Button>
      </Group>
      <Modal opened={confirmar} onClose={() => setConfirmar(false)} title="Desparear o gateway?">
        <Stack>
          <Text size="sm">
            A sessão do WhatsApp neste gateway é apagada. O número deixa de enviar até alguém ler um QR novo.
          </Text>
          <Group>
            <Button variant="subtle" onClick={() => setConfirmar(false)}>Cancelar</Button>
            <Button color="red" loading={desparear.isPending} onClick={confirmarDesparear}>
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
